// src/components/OrgUsersTable.jsx
//
// The ORG-ADMIN members table (OrgAdminPage). Not to be confused with
// platform/components/OrgUsersTable.jsx, which is the DreamHammer-side table
// of the same name for the platform admin surface.

import { Fragment } from "react";
import { UserX, UserCheck, AlertCircle } from "lucide-react";

import { ORG_ROLE_OPTIONS } from "../constants/orgRoles";

export default function OrgUsersTable({
  users = [],
  onUpdateUser,
  rowError = null, // { id, message } — failure from the parent's updateUser
}) {
  if (!users.length) {
    return (
      <div className="p-4 text-xs text-gray-400">
        No users in this organization yet.
      </div>
    );
  }

  return (
    <div className="border border-bg-border rounded overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-bg-soft text-gray-400">
          <tr>
            <th className="text-left px-3 py-2">Email</th>
            <th className="text-left px-3 py-2">Role</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-left px-3 py-2">Last Login</th>
          </tr>
        </thead>

        <tbody>
          {users.map(u => {
            const disabled = u.membership_status !== "active";
            const rowHasError = rowError?.id === u.user_id;

            return (
              <Fragment key={u.user_id}>
              <tr
                className="border-t border-bg-border hover:bg-bg-soft/40"
              >
                {/* Email */}
                <td className="px-3 py-2 text-gray-200">
                  {u.email}
                </td>

                {/* Role */}
                <td className="px-3 py-2">
                  <select
                    className="bg-bg border border-bg-border rounded px-1 py-0.5"
                    value={u.role}
                    disabled={disabled}
                    onChange={e =>
                      onUpdateUser(u.user_id, { role: e.target.value })
                    }
                  >
                    {ORG_ROLE_OPTIONS.map(r => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </td>

                {/* Status */}
                <td className="px-3 py-2">
                  <button
                    onClick={() =>
                      onUpdateUser(u.user_id, {
                        status: disabled ? "active" : "disabled",
                      })
                    }
                    className={`flex items-center gap-1 px-2 py-0.5 rounded border
                      ${disabled
                        ? "border-red-500/50 text-red-400"
                        : "border-green-500/50 text-green-400"
                      }`}
                  >
                    {disabled ? <UserX size={12} /> : <UserCheck size={12} />}
                    {disabled ? "Disabled" : "Active"}
                  </button>
                </td>

                {/* Last login */}
                <td className="px-3 py-2 text-gray-400">
                  {u.last_login_at
                    ? new Date(u.last_login_at).toLocaleString()
                    : "—"}
                </td>
              </tr>

              {/* Attached to the row it belongs to — no top border, so it
                  reads as part of that row rather than a new one. */}
              {rowHasError && (
                <tr>
                  <td colSpan={4} className="px-3 pb-2">
                    <span className="flex items-center gap-1.5 text-[11px] text-red-400">
                      <AlertCircle size={11} className="shrink-0" />
                      {rowError.message}
                    </span>
                  </td>
                </tr>
              )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
